/**
 * Sync core — pure, provider-agnostic logic for cross-device sync.
 *
 * Conflict strategy (v1): document-level last-write-wins on `updatedAt`.
 * Honest trade-off: a simultaneous edit on two offline devices keeps the
 * later one. Per-task CRDT merging is a future enhancement; the envelope
 * shape below leaves room for it.
 *
 * SECURITY INVARIANT: the synced snapshot NEVER contains subscription /
 * entitlement state, scan tokens, or API keys. Entitlements come only from
 * the billing provider on each device (restore purchases); keys stay local.
 */

import type { ArchivedTask, ImportStrategy, Task, WeekRecord } from '../types';
import { sameItem, type UncertainMatch } from '../engine/dedupe';
import type { Tag } from '../engine/tags';
import { applyTombstones, mergeBins, type BinEntry } from '../engine/bin';

/** The exact slice of state that syncs across devices. */
export interface SyncSnapshot {
  tasks: Record<string, Task>;
  archive: ArchivedTask[];
  weekHistory: WeekRecord[];
  importStrategy: ImportStrategy;
  onboarded: boolean;
  /**
   * Pending "is this the same task?" questions from a smart merge. Synced:
   * the user scans on one device and reviews on another. Optional for
   * migration safety — pre-existing cloud docs lack it.
   */
  mergeReview?: UncertainMatch[];
  /**
   * Epoch ms of the most recent "Start New Week" rollover. The merge treats
   * this as a WEEK BOUNDARY: completed work from before it belongs to a
   * closed week and must never be resurrected onto the new board by a
   * diverged device. Optional for migration safety.
   */
  weekStartedAt?: number;
  /** Tags (projects/roles/deliverables) — per-item newer-wins merge like
   *  tasks. Optional for migration safety. */
  tags?: Record<string, Tag>;
  /** The Bin: deleted tasks held for 7 days, restorable. Synced — entries
   *  double as deletion tombstones in the merge (see engine/bin.ts).
   *  Optional for migration safety. */
  bin?: BinEntry[];
}

/** What is stored in the cloud document. */
export interface SyncEnvelope {
  snapshot: SyncSnapshot;
  updatedAt: number;
  /** Schema version for forward migrations. */
  v: 1;
}

export interface SnapshotSource {
  tasks: Record<string, Task>;
  archive: ArchivedTask[];
  weekHistory: WeekRecord[];
  importStrategy: ImportStrategy;
  onboarded: boolean;
  mergeReview?: UncertainMatch[];
  weekStartedAt?: number;
  tags?: Record<string, Tag>;
  bin?: BinEntry[];
}

export function snapshotOf(state: SnapshotSource): SyncSnapshot {
  return {
    tasks: state.tasks,
    archive: state.archive,
    weekHistory: state.weekHistory,
    importStrategy: state.importStrategy,
    onboarded: state.onboarded,
    mergeReview: state.mergeReview ?? [],
    weekStartedAt: state.weekStartedAt ?? 0,
    tags: state.tags ?? {},
    bin: state.bin ?? [],
  };
}

/**
 * Recursively sort OBJECT keys so two structurally identical snapshots
 * serialise identically. Array order is preserved — it carries meaning
 * (archive, weekHistory, bin, mergeReview are ordered lists).
 *
 * Keys with `undefined` values are dropped, matching what JSON.stringify
 * already does, so a local `{a: undefined}` and a cloud `{}` (Firestore
 * rejects undefined; `sanitizeForSync` strips it) compare equal.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value !== null && typeof value === 'object') {
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(src).sort()) {
      const v = canonicalize(src[key]);
      if (v !== undefined) out[key] = v;
    }
    return out;
  }
  return value;
}

/**
 * Structural equality for snapshots.
 *
 * ⚠ WHY THIS IS NOT `JSON.stringify(a) === JSON.stringify(b)` — measured in
 * production 24 Aug 2026. Firestore does NOT preserve JavaScript object key
 * insertion order: a snapshot that round-trips through the cloud comes back
 * with its map fields reordered. Raw stringify therefore reported two
 * IDENTICAL snapshots as different, every single time, which meant:
 *
 *   push() never short-circuited -> device pushed -> the other device's
 *   listener fired -> it applied and pushed back -> forever.
 *
 * Live evidence on the founder's account: ~7 persisted writes per second
 * across two open contexts, `syncUpdatedAt` advancing on every cycle (so
 * real Firestore writes), with the ONLY difference between consecutive
 * snapshots being key order — identical task ids, identical values.
 *
 * The old code carried the comment "key-order sensitive in theory; stable
 * in practice because snapshots come from one code path". That was wrong:
 * the cloud is the second code path.
 *
 * Cost: canonicalize() sorts keys on each compare. At snapshot sizes this
 * is microseconds, and it runs where a NETWORK write was the alternative.
 */
export function snapshotsEqual(a: SyncSnapshot, b: SyncSnapshot): boolean {
  return JSON.stringify(canonicalize(a)) === JSON.stringify(canonicalize(b));
}

/** Apply the remote copy only when it is strictly newer than local. */
export function shouldApplyRemote(remoteUpdatedAt: number, localUpdatedAt: number): boolean {
  return remoteUpdatedAt > localUpdatedAt;
}

/**
 * Has THIS device made edits the cloud hasn't seen? True only when the
 * snapshots actually differ AND the local edit clock is ahead of the last
 * sync point. A device that is merely stale (no local edits) returns false
 * and should simply take the cloud copy.
 */
export function localDiverged(
  local: SyncSnapshot,
  remote: SyncSnapshot,
  syncUpdatedAt: number,
  lastLocalEditAt: number,
): boolean {
  if (snapshotsEqual(local, remote)) return false;
  if (lastLocalEditAt > syncUpdatedAt) return true;
  // Migration safety: devices persisted before edit-tracking existed carry
  // lastLocalEditAt = 0. If such a device holds tasks the cloud lacks,
  // merge rather than silently discard them.
  return (
    lastLocalEditAt === 0 && Object.keys(local.tasks).some((id) => !(id in remote.tasks))
  );
}

/**
 * Union-merge two diverged snapshots — the conflict answer that never
 * throws data away. Per task id, the copy with the newer edit stamp wins
 * (falling back to createdAt for pre-stamp tasks). Archives and week
 * history are unioned by id.
 *
 * Deletion tombstones (via the Bin, engine/bin.ts): a task deleted through
 * the bin carries a `deletedAt` tombstone that the merge honors — deletions
 * now propagate cleanly instead of resurrecting. The old "annoying beats
 * fatal" resurrection remains only for deletions that bypass the bin
 * (none in the current UI) or whose tombstone has aged out (7 days).
 */
/**
 * The pull-first decision a device makes when it attaches to an account's
 * cloud document (firebaseSync.attach). Pure so it is unit-tested.
 *
 *  - 'seed'          no cloud doc yet → local state seeds it
 *  - 'noop'          already equal → adopt the cloud stamp only
 *  - 'adopt-remote'  cloud is newer and local did not diverge → take cloud
 *  - 'merge'         both sides changed → merge, never discard
 *  - 'push-local'    local is at/ahead of the cloud stamp and differs
 *
 * ACCOUNT SWITCH (SEV-3, WRITE_PATHS.md, found 27 Sept 2026): when the
 * local copy belongs to a DIFFERENT account (`sameAccount === false`), it
 * must never be pushed over, or merged into, this account's cloud — the
 * stamps it carries are the other account's. A cloud doc wins outright.
 *
 *  - 'start-empty'   a DIFFERENT account with no cloud doc yet → the new
 *                    account starts with an EMPTY board. (Until 11 Oct 2026
 *                    this returned 'seed', which copied the founder's whole
 *                    board into the App Review demo account; the clean-up
 *                    that followed emptied his real account — see
 *                    docs/DATA_SAFETY_PLAN.md. A board belongs to the
 *                    account that made it; it never travels by sign-in.)
 */
export type InitialSyncDecision =
  | 'seed'
  | 'start-empty'
  | 'noop'
  | 'adopt-remote'
  | 'merge'
  | 'push-local';

export function decideInitialSync(args: {
  local: SyncSnapshot;
  remote: SyncEnvelope | null;
  syncUpdatedAt: number;
  lastLocalEditAt: number;
  sameAccount: boolean;
}): InitialSyncDecision {
  const { local, remote, syncUpdatedAt, lastLocalEditAt, sameAccount } = args;
  if (!remote) return sameAccount ? 'seed' : 'start-empty';
  if (!sameAccount) return 'adopt-remote';
  if (snapshotsEqual(local, remote.snapshot)) return 'noop';
  if (remote.updatedAt > syncUpdatedAt) {
    return localDiverged(local, remote.snapshot, syncUpdatedAt, lastLocalEditAt)
      ? 'merge'
      : 'adopt-remote';
  }
  return 'push-local';
}

/**
 * The board a brand-new account starts with on a device that last synced a
 * DIFFERENT account ('start-empty'). Device preferences (onboarding seen,
 * import strategy) stay; every piece of the previous account's work goes.
 */
export function emptySnapshotFor(local: SyncSnapshot): SyncSnapshot {
  return {
    tasks: {},
    archive: [],
    weekHistory: [],
    importStrategy: local.importStrategy,
    onboarded: local.onboarded,
    mergeReview: [],
    weekStartedAt: 0,
    tags: {},
    bin: [],
  };
}

/**
 * WIPE GUARD (11 Oct 2026). The last line of defence on the push path: a
 * push that would erase a user's history from the cloud is refused, whatever
 * caused it. Returns the reason in plain words (shown verbatim in Settings),
 * or null when the push is an ordinary edit.
 *
 * `cloud` is the RAW snapshot this device last saw in the cloud (unparsed —
 * read leniently). Two shapes are a wipe, never an edit, in the current UI:
 *
 *  1. Mastery history shrinks. weekHistory only ever grows (Start New Week
 *     prepends one and caps at 52, so its length never drops).
 *  2. The board goes from 3+ tasks to none with NO record of where they
 *     went: no new Bin entry (deletes), no new archive entry (completions),
 *     no new week (rollover). "Erase this device" before 11 Oct produced
 *     exactly this, and it reached the founder's real account.
 */
export function wipeGuardReason(cloud: unknown, outgoing: SyncSnapshot): string | null {
  if (!cloud || typeof cloud !== 'object') return null;
  const c = cloud as Record<string, unknown>;
  const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
  const keys = (v: unknown): string[] =>
    v && typeof v === 'object' && !Array.isArray(v) ? Object.keys(v) : [];
  const ids = (list: unknown[], pick: (x: Record<string, unknown>) => unknown): Set<string> =>
    new Set(
      list
        .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
        .map((x) => String(pick(x))),
    );

  const cloudWeeks = arr(c.weekHistory);
  if (outgoing.weekHistory.length < cloudWeeks.length) {
    const lost = cloudWeeks.length - outgoing.weekHistory.length;
    return `Sync paused to protect your Mastery history: this device would remove ${lost} week${lost === 1 ? '' : 's'} of it from your account (${cloudWeeks.length} → ${outgoing.weekHistory.length}). Nothing was overwritten, and your account's copy has been brought back to this device.`;
  }

  const cloudTasks = keys(c.tasks).length;
  if (cloudTasks >= 3 && Object.keys(outgoing.tasks).length === 0) {
    const weekIds = ids(cloudWeeks, (w) => w.id);
    const archiveIds = ids(arr(c.archive), (a) => a.taskId);
    const binIds = ids(arr(c.bin), (b) => {
      const t = b.task as Record<string, unknown> | undefined;
      return `${t?.id}:${b.deletedAt}`;
    });
    const newWeek = outgoing.weekHistory.some((w) => !weekIds.has(String(w.id)));
    const newArchive = outgoing.archive.some((a) => !archiveIds.has(String(a.taskId)));
    const newBin = (outgoing.bin ?? []).some((b) => !binIds.has(`${b.task.id}:${b.deletedAt}`));
    if (!newWeek && !newArchive && !newBin) {
      return `Sync paused to protect your account: this device's board is empty, but your account holds ${cloudTasks} tasks. Nothing was overwritten, and your account's copy has been brought back to this device.`;
    }
  }
  return null;
}

export function mergeSnapshots(
  local: SyncSnapshot,
  remote: SyncSnapshot,
  now: number = Date.now(),
): SyncSnapshot {
  const stamp = (t: Task) => t.editedAt ?? t.createdAt;
  const unionTasks: Record<string, Task> = { ...remote.tasks };
  for (const [id, lt] of Object.entries(local.tasks)) {
    const rt = unionTasks[id];
    if (!rt || stamp(lt) >= stamp(rt)) unionTasks[id] = lt;
  }
  // Bin: union (newer deletion wins), then apply tombstones — a deletion
  // newer than a task's last edit removes it from the merged board; an edit
  // or restore newer than the deletion keeps the task and drops the entry.
  const mergedBin = mergeBins(local.bin ?? [], remote.bin ?? [], now);
  const { tasks, bin } = applyTombstones(unionTasks, mergedBin);
  // The most recent rollover on EITHER side is the week boundary. Completed
  // work from before it was retired into that week's record — a diverged
  // device's stale copy must not resurrect it onto the new board. (A task
  // completed AFTER the boundary keeps a newer stamp and survives, which is
  // correct: it was completed in the new week.)
  const boundary = Math.max(local.weekStartedAt ?? 0, remote.weekStartedAt ?? 0);
  for (const [id, t] of Object.entries(tasks)) {
    if (t.done && stamp(t) < boundary) delete tasks[id];
  }

  const archive = [...remote.archive].filter((a) => a.completedAt >= boundary);
  for (const a of local.archive) {
    if (a.completedAt >= boundary && !archive.some((r) => r.taskId === a.taskId)) {
      archive.push(a);
    }
  }
  const weekHistory = [...remote.weekHistory];
  for (const w of local.weekHistory) {
    if (!weekHistory.some((r) => r.id === w.id)) weekHistory.push(w);
  }
  weekHistory.sort((a, b) => b.endedAt - a.endedAt);

  // Pending review questions: union both sides, then auto-resolve any whose
  // item now confidently exists on the merged board (it was answered "add"
  // on the other device — re-asking would create a duplicate). A question
  // answered "same — skip" elsewhere may reappear once; annoying beats
  // fatal, and answering it again is one tap.
  const rKey = (u: UncertainMatch) =>
    `${u.item.text}::${u.matchText}::${u.kind ?? 'similar'}`.toLowerCase();
  const union: UncertainMatch[] = [...(local.mergeReview ?? [])];
  for (const r of remote.mergeReview ?? []) {
    if (!union.some((l) => rKey(l) === rKey(r))) union.push(r);
  }
  const openTexts = Object.values(tasks)
    .filter((t) => !t.done)
    .map((t) => t.text);
  const mergeReview = union.filter(
    // 'completed' questions stay even when an open match exists — answering
    // "done" is exactly what completes that open task.
    (u) => u.kind === 'completed' || !openTexts.some((t) => sameItem(t, u.item.text)),
  );

  // Tags: same union discipline as tasks — per-item newer editedAt wins.
  const tagStamp = (t: Tag) => t.editedAt ?? t.createdAt;
  const tags: Record<string, Tag> = { ...(remote.tags ?? {}) };
  for (const [id, lt] of Object.entries(local.tags ?? {})) {
    const rt = tags[id];
    if (!rt || tagStamp(lt) >= tagStamp(rt)) tags[id] = lt;
  }

  return {
    tasks,
    archive,
    weekHistory: weekHistory.slice(0, 52),
    importStrategy: local.importStrategy,
    onboarded: local.onboarded || remote.onboarded,
    mergeReview,
    weekStartedAt: boundary,
    tags,
    bin,
  };
}

export function makeEnvelope(snapshot: SyncSnapshot, updatedAt: number): SyncEnvelope {
  return { snapshot, updatedAt, v: 1 };
}

/**
 * Firestore rejects `undefined` field values outright. A JSON round-trip
 * strips undefined-valued keys (and anything else non-serializable), which
 * is exactly the contract of the sync document.
 */
export function sanitizeForSync<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Runtime validation of an untrusted cloud document. */
export function parseEnvelope(raw: unknown): SyncEnvelope | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Partial<SyncEnvelope>;
  if (e.v !== 1) return null;
  if (typeof e.updatedAt !== 'number' || !Number.isFinite(e.updatedAt)) return null;
  const s = e.snapshot as Partial<SyncSnapshot> | undefined;
  if (typeof s !== 'object' || s === null) return null;
  if (typeof s.tasks !== 'object' || s.tasks === null) return null;
  if (!Array.isArray(s.archive) || !Array.isArray(s.weekHistory)) return null;
  if (s.importStrategy !== 'merge' && s.importStrategy !== 'replace') return null;
  return {
    v: 1,
    updatedAt: e.updatedAt,
    snapshot: {
      tasks: s.tasks as Record<string, Task>,
      archive: s.archive as ArchivedTask[],
      weekHistory: s.weekHistory as WeekRecord[],
      importStrategy: s.importStrategy,
      onboarded: s.onboarded === true,
      // Tolerant of older docs (absent) and malformed entries (dropped).
      mergeReview: Array.isArray(s.mergeReview)
        ? (s.mergeReview as UncertainMatch[]).filter(
            (u) =>
              typeof u === 'object' &&
              u !== null &&
              typeof u.matchText === 'string' &&
              typeof u.item === 'object' &&
              u.item !== null &&
              typeof u.item.text === 'string',
          )
        : [],
      weekStartedAt:
        typeof s.weekStartedAt === 'number' && Number.isFinite(s.weekStartedAt)
          ? s.weekStartedAt
          : 0,
      // Tolerant of older docs (absent) — malformed entries dropped.
      tags:
        typeof s.tags === 'object' && s.tags !== null
          ? Object.fromEntries(
              Object.entries(s.tags as Record<string, Tag>).filter(
                ([, t]) =>
                  typeof t === 'object' &&
                  t !== null &&
                  typeof t.name === 'string' &&
                  typeof t.color === 'string',
              ),
            )
          : {},
      // Tolerant of older docs (absent) — malformed entries dropped.
      bin: Array.isArray(s.bin)
        ? (s.bin as BinEntry[]).filter(
            (e) =>
              typeof e === 'object' &&
              e !== null &&
              typeof e.deletedAt === 'number' &&
              Number.isFinite(e.deletedAt) &&
              typeof e.task === 'object' &&
              e.task !== null &&
              typeof e.task.id === 'string' &&
              typeof e.task.text === 'string',
          )
        : [],
    },
  };
}

export type SyncStatus = 'disabled' | 'signed-out' | 'syncing' | 'error';

/**
 * Apple sign-in failure → the sentence the user sees (7 Oct 2026).
 *
 * Firebase runs "one account per email". A tester whose account (and
 * lifetime grant, `users/{uid}/alpha/grant`) was made with Google, who then
 * taps "Sign in with Apple" sharing the same email, gets
 * `auth/account-exists-with-different-credential` — and until now saw that
 * raw string. The message names the way back in; the code stays appended so
 * the next failure still diagnoses itself (CLAUDE.md §2). Linking the two
 * credentials into one account is the full fix and is scoped separately.
 *
 * Pure: takes whatever was thrown, never throws.
 */
/**
 * Apple/Google account guard (10 Oct 2026).
 *
 * ZoneIn accounts are keyed by sign-in, and Apple can hand ZoneIn a
 * different address from the one a tester uses with Google (a Hotmail
 * Apple ID, or "Hide My Email"). Signing in with Apple then silently
 * CREATES a new, empty account — no tasks, no lifetime grant — and the
 * tester believes their access was lost. So on iPhone, an Apple sign-in
 * that would create a new account is HELD and the user chooses:
 * use the sign-in they already have, or start fresh.
 */
export type AppleSignInDecision = 'continue' | 'ask';
export function appleSignInDecision(isNewUser: boolean | undefined): AppleSignInDecision {
  return isNewUser === true ? 'ask' : 'continue';
}

/** Offer "Also allow Sign in with Apple" only on iPhone, to a signed-in account without it. */
export function canLinkApple(providers: readonly string[] | undefined, ios: boolean): boolean {
  if (!ios || !providers || providers.length === 0) return false;
  return !providers.includes('apple.com');
}

/** Linking Apple to the signed-in account: the two expected refusals in plain words, else verbatim (§2). */
export function describeAppleLinkError(err: unknown): string {
  const code =
    typeof err === 'object' && err !== null && typeof (err as { code?: unknown }).code === 'string'
      ? (err as { code: string }).code
      : '';
  if (code === 'auth/credential-already-in-use') {
    return 'This Apple ID already has its own ZoneIn account, so it can’t be added to this one. Keep using the sign-in you’re using now. (auth/credential-already-in-use)';
  }
  if (code === 'auth/provider-already-linked') {
    return 'Sign in with Apple is already set up for this account. (auth/provider-already-linked)';
  }
  return err instanceof Error ? `${err.name}: ${err.message}` : String(err);
}

export function describeAppleSignInError(err: unknown): string {
  const code =
    typeof err === 'object' && err !== null && typeof (err as { code?: unknown }).code === 'string'
      ? (err as { code: string }).code
      : '';
  if (code === 'auth/account-exists-with-different-credential') {
    return 'This email already has a ZoneIn account that signs in with Google. Use “Sign in with Google” to reach your tasks and any lifetime access. (auth/account-exists-with-different-credential)';
  }
  return err instanceof Error ? `${err.name}: ${err.message}` : String(err);
}

/**
 * The provider to offer first for an account remembered on this device
 * (13 Oct 2026, one-tap "Sign in as …"): Google, then Apple, then password.
 * Pure — lives here, not in firebaseSync, so its test loads nothing heavy
 * (the first version imported firebaseSync and timed out at 5 s on the
 * founder's machine).
 */
export function primaryProvider(providerIds: readonly string[]): string {
  for (const p of ['google.com', 'apple.com', 'password']) if (providerIds.includes(p)) return p;
  return providerIds[0] ?? 'google.com';
}
